import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import InventoryLayout from "@/components/inventory/InventoryLayout";
import { SelectField, SecondaryButton } from "@/components/inventory/Modal";
import { api, formatApiError } from "@/lib/api";

const movementLabel = {
  opening: "Opening",
  adjustment_in: "Adjust +",
  adjustment_out: "Adjust −",
  transfer_in: "Transfer in",
  transfer_out: "Transfer out",
};

const movementTone = {
  opening: "text-slate-700 bg-slate-100",
  adjustment_in: "text-[#039855] bg-[#039855]/10",
  adjustment_out: "text-[#D92D20] bg-[#D92D20]/10",
  transfer_in: "text-[#002FA7] bg-[#002FA7]/10",
  transfer_out: "text-[#0A0A0B] bg-slate-200",
};

export default function MovementsPage() {
  const [rows, setRows] = useState([]);
  const [products, setProducts] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    product_id: "",
    warehouse_id: "",
    movement_type: "",
  });

  const productMap = React.useMemo(
    () => Object.fromEntries(products.map((p) => [p.id, p])),
    [products]
  );
  const warehouseMap = React.useMemo(
    () => Object.fromEntries(warehouses.map((w) => [w.id, w])),
    [warehouses]
  );

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.product_id) params.product_id = filters.product_id;
      if (filters.warehouse_id) params.warehouse_id = filters.warehouse_id;
      if (filters.movement_type) params.movement_type = filters.movement_type;
      const [mRes, pRes, wRes] = await Promise.all([
        api.get("/inventory/movements", { params }),
        products.length
          ? { data: products }
          : api.get("/inventory/products", { params: { limit: 500 } }),
        warehouses.length
          ? { data: warehouses }
          : api.get("/inventory/warehouses", { params: { limit: 500 } }),
      ]);
      setRows(mRes.data);
      if (!products.length) setProducts(pRes.data);
      if (!warehouses.length) setWarehouses(wRes.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <InventoryLayout title="Movements">
      <p className="text-xs text-slate-500 leading-relaxed mb-6 max-w-2xl">
        Immutable audit trail of every stock event. Purchases, sales, GRN and
        invoice modules (future sprints) will append their own movements here
        via the same service layer.
      </p>

      <div className="mb-6 grid md:grid-cols-4 gap-3 items-end">
        <SelectField
          label="Product"
          testid="mv-filter-product"
          value={filters.product_id}
          onChange={(e) => setFilters({ ...filters, product_id: e.target.value })}
        >
          <option value="">All</option>
          {products.map((p) => <option key={p.id} value={p.id}>{p.sku} · {p.name}</option>)}
        </SelectField>
        <SelectField
          label="Warehouse"
          testid="mv-filter-warehouse"
          value={filters.warehouse_id}
          onChange={(e) => setFilters({ ...filters, warehouse_id: e.target.value })}
        >
          <option value="">All</option>
          {warehouses.map((w) => <option key={w.id} value={w.id}>{w.code} · {w.name}</option>)}
        </SelectField>
        <SelectField
          label="Type"
          testid="mv-filter-type"
          value={filters.movement_type}
          onChange={(e) => setFilters({ ...filters, movement_type: e.target.value })}
        >
          <option value="">All</option>
          <option value="opening">Opening</option>
          <option value="adjustment_in">Adjustment (in)</option>
          <option value="adjustment_out">Adjustment (out)</option>
          <option value="transfer_in">Transfer (in)</option>
          <option value="transfer_out">Transfer (out)</option>
        </SelectField>
        <SecondaryButton testid="mv-apply-filters" onClick={load}>Apply filters</SecondaryButton>
      </div>

      <div className="border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]">
          <thead>
            <tr className="border-b border-slate-200 text-left">
              <th className="px-4 py-3 text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">When</th>
              <th className="px-4 py-3 text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">Type</th>
              <th className="px-4 py-3 text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">Product</th>
              <th className="px-4 py-3 text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">Warehouse</th>
              <th className="px-4 py-3 text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500 text-right">Qty</th>
              <th className="px-4 py-3 text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">Reference</th>
              <th className="px-4 py-3 text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">Notes</th>
            </tr>
          </thead>
          <tbody data-testid="mv-tbody">
            {loading ? (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-500 text-sm">Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-500 text-sm">No movements found.</td></tr>
            ) : (
              rows.map((r) => {
                const prod = productMap[r.product_id];
                const wh = warehouseMap[r.warehouse_id];
                return (
                  <tr key={r.id} className="border-b border-slate-100 last:border-b-0">
                    <td className="px-4 py-3 text-xs text-slate-500 whitespace-nowrap">
                      {new Date(r.created_at).toLocaleString()}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        data-testid={`mv-type-${r.id}`}
                        className={`inline-block px-2 py-1 text-[10px] uppercase tracking-[0.15em] font-semibold ${movementTone[r.movement_type] || "text-slate-700 bg-slate-100"}`}
                      >
                        {movementLabel[r.movement_type] || r.movement_type}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-mono text-xs mr-1">{prod?.sku || r.product_id.slice(0, 8)}</span>
                      · {prod?.name || "—"}
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-mono text-xs mr-1">{wh?.code || r.warehouse_id.slice(0, 8)}</span>
                      · {wh?.name || "—"}
                    </td>
                    <td
                      className={`px-4 py-3 text-right font-semibold ${r.qty > 0 ? "text-[#039855]" : r.qty < 0 ? "text-[#D92D20]" : ""}`}
                      data-testid={`mv-qty-${r.id}`}
                    >
                      {r.qty > 0 ? `+${r.qty}` : r.qty}
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-500 font-mono">
                      {r.reference_id ? `${r.reference_type}:${r.reference_id.slice(0, 8)}` : "—"}
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-600">{r.notes || "—"}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </InventoryLayout>
  );
}
